<script>
import ChoiceExam from "@/components/exam/choice-exam.vue";
import EssayExam from "@/components/exam/essay-exam.vue";
import BaseLoading from "@/components/CustomComponent/shared/Loading.vue";
import certificates from "@/components/certificates.vue";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useSharedStore } from "@/stores/shared-store";
import { useExamFetchStore } from "@/stores/exam/exam-fetch-store";
import { useExamDataStore } from "@/stores/exam/exam-data-store";
import { useCourseFetchStore } from "@/stores/course/course-fetch-store";
import { useAuthStore } from "@/stores/auth-store";
import Swal from "sweetalert2";

export default {
  props: {
    courseID: {
      type: [String, Number],
    },
    examID: {
      type: [String, Number],
    },
  },
  components: {
    ChoiceExam,
    EssayExam,
    BaseLoading,
    certificates,
  },
  data() {
    return {
      // You can add any data properties you need here
      currentIndex: 0,
      currentQuestion: null,
      typeQuestion: true, // kalo true = Pilihan ganda kalo false = Essay
      timer: "00:00:00",
      timerInterval: null,
      endTime: new Date(new Date().getTime() + 5 * 60 * 1000),
      totalQuestions: 0,
    };
  },
  computed: {
    authStore() {
      return useAuthStore();
    },
    sharedStore() {
      return useSharedStore();
    },
    examFetchStore() {
      return useExamFetchStore();
    },
    examDataStore() {
      return useExamDataStore();
    },
    courseFetchStore() {
      return useCourseFetchStore();
    },
    effectiveCourseID() {
      return (
        this.courseID ||
        this.$route.params.courseID ||
        this.$route.params.course_id
      );
    },
    studentName() {
      const user = this.authStore.currentUser;
      if (!user) return "Student";
      if (user.nickname) return user.nickname;
      const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
      return fullName || user.username || "Student";
    },
    courseTitle() {
      return (
        this.courseFetchStore.basicCourseDetail?.title ||
        this.examDataStore.submissionInfo?.examTitle ||
        "Course"
      );
    },
    instructorName() {
      return (
        this.authStore.userName(
          this.courseFetchStore.basicCourseDetail?.instructor
        ) || "Instructor"
      );
    },
    isStudent() {
      return this.authStore.role?.toLowerCase() === "student";
    },
    isPrincipal() {
      return this.authStore.role?.toLowerCase() === "principal";
    },
    isInstructor() {
      return this.authStore.role?.toLowerCase() === "teacher";
    },
    isPrincipalOrInstructor() {
      return this.isPrincipal || this.isInstructor;
    },
    pageTitle() {
      return this.isPrincipalOrInstructor ? "Preview Exam" : "Exam";
    },
    hasQuestions() {
      return (this.examFetchStore.examQuestions?.length || 0) > 0;
    },
    showTimer() {
      return this.isStudent && !!this.examDataStore.submissionInfo?.endTime;
    },
  },
  async mounted() {
    await this.examDataStore.loadFromLocalStorage();

    // Principal & teacher preview soal; student kerjakan ujian
    if (this.isPrincipalOrInstructor) {
      await this.examFetchStore.fetchQuestionsByExam(this.examID);
    } else if (this.isStudent) {
      if (this.effectiveCourseID) {
        this.courseFetchStore.fetchCourseDetailBasic(this.effectiveCourseID);
      }

      await this.examFetchStore.fetchStudentQuestionsByExam(this.examID);

      await this.examDataStore.fetchSubmission(
        this.examDataStore.submission?.submissionID
      );

      if (
        !this.examDataStore.examAnswer ||
        this.examDataStore.examAnswer.length === 0
      ) {
        this.examDataStore.initAnswers();
      }

      if (this.examDataStore.submissionInfo?.endTime) {
        // pastikan format Date
        this.endTime = new Date(this.examDataStore.submissionInfo.endTime);
        this.startTimer();
      }
    }

    if (this.examFetchStore.examQuestions.length > 0) {
      this.currentQuestion = this.examFetchStore.examQuestions[0];
      this.typeQuestion =
        this.currentQuestion.questionType === "MultipleChoice";
    }

    this.totalQuestions = this.examFetchStore.examQuestions.length;
  },
  beforeUnmount() {
    if (this.timerInterval) clearInterval(this.timerInterval);
  },
  methods: {
    navigateBack() {
      if (this.courseID) {
        this.$router.push({
          name: "CourseDetail",
          params: {
            course_id: this.courseID,
          },
        });
        return;
      }
      this.$router.back();
    },
    async handleBack() {
      if (this.isStudent && this.hasQuestions) {
        const result = await Swal.fire({
          title: "Leave exam?",
          text: "Your answers are saved for this session, but you will leave the exam page. Continue?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, leave",
          cancelButtonText: "Stay",
          confirmButtonColor: "#ff4667",
        });
        if (!result.isConfirmed) return;
      }

      this.navigateBack();
    },
    startTimer() {
      if (!this.endTime) return;

      const updateTimer = () => {
        const now = new Date();
        let diff = this.endTime - now; // selisih dalam ms

        if (diff < 0) diff = 0;

        const hrs = String(Math.floor(diff / (1000 * 60 * 60))).padStart(
          2,
          "0"
        );
        const mins = String(
          Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
        ).padStart(2, "0");
        const secs = String(Math.floor((diff % (1000 * 60)) / 1000)).padStart(
          2,
          "0"
        );

        this.timer = `${hrs}:${mins}:${secs}`;

        if (diff === 0 && this.timerInterval) {
          clearInterval(this.timerInterval);
          this.onTimeUp();
        }
      };

      updateTimer(); // langsung tampilkan
      this.timerInterval = setInterval(updateTimer, 1000);
    },
    onTimeUp() {
      this.handelTimeUp();
    },
    isQuestionAnswered(questionID) {
      if (!this.examDataStore.examAnswer) return false;
      const item = this.examDataStore.examAnswer.find(
        (a) => String(a.question) === String(questionID)
      );
      return Boolean(item && item.answer !== null && item.answer !== "");
    },
    goToQuestion(index) {
      this.currentIndex = index;
      this.currentQuestion = this.examFetchStore.examQuestions[index];
      this.typeQuestion =
        this.currentQuestion.questionType === "MultipleChoice";
    },
    nextQuestion() {
      if (this.currentIndex < this.totalQuestions - 1) {
        this.goToQuestion(this.currentIndex + 1);
      }
    },
    prevQuestion() {
      if (this.currentIndex > 0) {
        this.goToQuestion(this.currentIndex - 1);
      }
    },
    async generateAndSaveCertificate() {
      try {
        if (!this.$refs.certComponent?.certRef) {
          console.warn("certComponent ref tidak ditemukan");
          return;
        }

        const element = this.$refs.certComponent.certRef;

        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
        });

        const imgData = canvas.toDataURL("image/png");

        const pdf = new jsPDF({
          orientation: canvas.width > canvas.height ? "landscape" : "portrait",
          unit: "px",
          format: [canvas.width, canvas.height],
        });

        pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);

        const pdfBlob = pdf.output("blob");

        const studentID = this.authStore.currentUser?.id;
        const targetCourseID = this.effectiveCourseID;
        await this.examDataStore.uploadCertificate(
          pdfBlob,
          targetCourseID,
          studentID
        );
      } catch (err) {
        console.error("Gagal auto-generate sertifikat:", err);
      }
    },
    async handelFinishExam() {
      if (this.isPrincipalOrInstructor) {
        // Preview saja → balik ke detail course
        this.examDataStore.clearSession();
        this.$router.push({
          name: "CourseDetail",
          params: {
            course_id: this.courseID,
          },
        });
        return;
      }

      const result = await Swal.fire({
        title: "Konfirmasi",
        text: "Apakah Anda yakin ingin menyelesaikan ujian? Jawaban tidak bisa diubah lagi.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Ya, selesai",
        cancelButtonText: "Batal",
      });
      if (result.isConfirmed) {
        try {
          // Panggil submitAnswers dari store
          const response = await this.examDataStore.submitAnswers();

          this.examDataStore.clearSession();

          if (response?.status === "Passed") {
            // Auto generate certificate jika lulus
            await this.generateAndSaveCertificate();
            await Swal.fire({
              title: "Selamat! Anda Lulus!",
              text: `Ujian berhasil diselesaikan dengan nilai ${response.totalScore ?? response.multipleChoiceScore}. Sertifikat Anda telah otomatis diterbitkan!`,
              icon: "success",
              confirmButtonText: "Lihat Hasil / Selesai",
            });
          } else if (response?.status === "Failed") {
            await Swal.fire({
              title: "Ujian Selesai",
              text: `Nilai Anda: ${response.totalScore ?? response.multipleChoiceScore}. Anda belum mencapai passing score.`,
              icon: "info",
              confirmButtonText: "OK",
            });
          } else {
            await Swal.fire({
              title: "Berhasil",
              text: "Jawaban ujian berhasil dikirim! Menunggu pemeriksaan dari instruktur.",
              icon: "success",
              confirmButtonText: "OK",
            });
          }

          // Redirect atau aksi lain setelah submit
          this.$router.push("/students/student-exam");
        } catch (error) {
          console.error("Gagal submit jawaban:", error);
          Swal.fire({
            title: "Gagal",
            text: "Terjadi kesalahan saat mengirim jawaban. Silakan coba lagi.",
            icon: "error",
          });
        }
      }
    },
    async handelTimeUp() {
      try {
        // Kasih info ke user
        await Swal.fire({
          title: "Waktu Habis",
          text: "Waktu ujian sudah berakhir. Jawaban Anda akan otomatis dikirim.",
          icon: "info",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          timer: 3000, // 3 detik biar auto close
        });

        // Submit jawaban
        const response = await this.examDataStore.submitAnswers();

        // Clear local session
        this.examDataStore.clearSession();

        if (response?.status === "Passed") {
          await this.generateAndSaveCertificate();
          await Swal.fire({
            title: "Selamat! Anda Lulus!",
            text: `Ujian berhasil diselesaikan dengan nilai ${response.totalScore ?? response.multipleChoiceScore}. Sertifikat Anda telah otomatis diterbitkan!`,
            icon: "success",
            confirmButtonText: "Selesai",
          });
        } else if (response?.status === "Failed") {
          await Swal.fire({
            title: "Ujian Selesai",
            text: `Nilai Anda: ${response.totalScore ?? response.multipleChoiceScore}. Anda belum mencapai passing score.`,
            icon: "info",
            confirmButtonText: "OK",
          });
        } else {
          await Swal.fire({
            title: "Berhasil",
            text: "Jawaban ujian berhasil dikirim! Menunggu pemeriksaan dari instruktur.",
            icon: "success",
            confirmButtonText: "OK",
          });
        }

        // Redirect
        this.$router.push("/students/student-exam");
      } catch (error) {
        console.error("Gagal auto submit saat time up:", error);

        Swal.fire({
          title: "Gagal",
          text: "Terjadi kesalahan saat auto submit jawaban. Silakan hubungi admin.",
          icon: "error",
        });
      }
    },
  },
};
</script>
<template>
  <div class="exam-page">
    <!-- Header: back + title + timer -->
    <section class="exam-header border-bottom bg-white">
      <div
        class="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 px-md-4 py-3"
      >
        <div class="d-flex align-items-center gap-3 min-w-0">
          <button
            type="button"
            class="btn btn-link text-decoration-none color-main p-0 d-inline-flex align-items-center exam-back-btn"
            @click="handleBack"
            aria-label="Back to course"
          >
            <i class="isax isax-arrow-left me-1" aria-hidden="true"></i>
            <span>Back to Course</span>
          </button>
          <span class="text-muted d-none d-sm-inline">|</span>
          <h5 class="mb-0 text-truncate fw-semibold">{{ pageTitle }}</h5>
        </div>

        <div
          v-if="isStudent"
          class="d-flex align-items-center gap-2 exam-timer"
          :class="{ 'text-muted': !showTimer }"
          aria-live="polite"
        >
          <i class="isax isax-clock me-1" aria-hidden="true"></i>
          <span class="fw-semibold font-monospace">{{ timer }}</span>
        </div>
      </div>
    </section>

    <!-- Empty state -->
    <section
      v-if="!sharedStore.loading && !hasQuestions"
      class="exam-empty text-center py-5 px-3"
    >
      <div class="mx-auto" style="max-width: 420px">
        <h6 class="mb-2">No questions available</h6>
        <p class="text-muted mb-4">
          This exam does not have any questions yet. Return to the course to
          continue.
        </p>
        <button type="button" class="btn btn-main px-4" @click="navigateBack">
          <i class="isax isax-arrow-left me-1" aria-hidden="true"></i>
          Back to Course
        </button>
      </div>
    </section>

    <!-- Main content -->
    <section v-else-if="hasQuestions" class="exam-body row g-0">
      <!-- Question number sidebar -->
      <aside class="col-12 col-md-3 border-end py-3">
        <p class="px-4 mb-2 text-muted small fw-semibold text-uppercase">
          Questions
        </p>
        <div class="d-flex py-2 px-4 gap-2 flex-wrap">
          <button
            v-for="(question, index) in examFetchStore.examQuestions"
            :key="question.questionID"
            type="button"
            @click="goToQuestion(index)"
            class="wh-nomor d-flex justify-content-center align-items-center rounded-2 border-0"
            :class="
              question === currentQuestion
                ? 'bg-main text-white'
                : isQuestionAnswered(question.questionID)
                ? 'bg-answered text-dark border border-danger-subtle'
                : 'bg-secondary-subtle text-dark'
            "
            :aria-label="`Go to question ${index + 1}`"
            :aria-current="question === currentQuestion ? 'true' : undefined"
          >
            <span class="fw-bolder">{{ index + 1 }}</span>
          </button>
        </div>
      </aside>

      <!-- Question area -->
      <div class="col-12 col-md-9 d-flex flex-column py-3">
        <template v-if="currentQuestion">
          <ChoiceExam
            v-if="currentQuestion.questionType === 'MultipleChoice'"
            :key="currentQuestion.questionID"
            :quiz="currentQuestion"
            :preview="isPrincipalOrInstructor"
          />
          <EssayExam
            v-else
            :key="currentQuestion.questionID"
            :quiz="currentQuestion"
            :preview="isPrincipalOrInstructor"
          />
        </template>

        <!-- Navigation Buttons -->
        <div
          class="d-flex justify-content-end mt-auto pt-3 border-top gap-2 gap-md-3 px-3 px-md-5"
        >
          <button
            type="button"
            class="btn btn-outline-primary px-4"
            @click="prevQuestion"
            :disabled="currentIndex === 0"
          >
            Previous
          </button>
          <button
            v-if="currentIndex < totalQuestions - 1"
            type="button"
            class="btn btn-outline-primary px-4"
            @click="nextQuestion"
          >
            Next
          </button>
          <button
            v-else
            type="button"
            class="btn btn-success px-4"
            @click="handelFinishExam"
          >
            Finish
          </button>
        </div>
      </div>
    </section>

    <!-- Off-screen container agar html2canvas bisa merender elemen sertifikat dengan ukuran nyata -->
    <div
      v-if="isStudent"
      style="position: fixed; left: -9999px; top: 0; z-index: -1000; width: 1100px;"
    >
      <certificates
        ref="certComponent"
        :name="studentName"
        :company="authStore.currentUser?.company || authStore.currentUser?.cmp_code"
        :course="courseTitle"
        :date="sharedStore.formatTanggal(new Date())"
        :instructor="instructorName"
      />
    </div>
  </div>
  <BaseLoading v-show="sharedStore.loading"></BaseLoading>
</template>
<style scoped>
.bg-main {
  background-color: #ff4667;
}

.color-main {
  color: #ff4667;
}

.btn-main {
  background-color: #ff4667;
  border-color: #ff4667;
  color: #fff;
}

.btn-main:hover,
.btn-main:focus {
  background-color: #e63d5c;
  border-color: #e63d5c;
  color: #fff;
}

.exam-back-btn:hover,
.exam-back-btn:focus {
  color: #e63d5c !important;
}

.wh-nomor {
  width: 40px;
  height: 40px;
  min-width: 40px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.wh-nomor:hover:not(.bg-main) {
  background-color: #ffe0e6 !important;
}

.bg-answered {
  background-color: #ffdbe2;
}

.exam-timer {
  font-variant-numeric: tabular-nums;
}
</style>
